import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import { getFanwarsLineup, patchFanwarsLineup } from '../../services/fanwarsLineupApi';
import {
  buildInitialSystemFive,
  buildOpponentPickerPool,
  findSnapshotEntry,
  formatImpactCount,
  formatImpactValue,
  franchiseLabel,
  getDisplayEntry,
  sideChipLabel,
  normalizeLineupPayload,
  opponentTeamId,
  orderedIdsByPriority,
  playerPrimaryName,
  sortConfigsForMatch,
  systemSelectedEntries,
  systemSnapshotSource,
  userPickEntries,
  userPickSnapshotSource,
} from './fanwarsLineupModel';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const IMPACT_COLUMNS = [
  {
    key: 'avg',
    label: 'Avg last 3',
    tip: 'Average impact_score over the performance rows in the window, not a sum of three matches.',
  },
  {
    key: 'rows',
    label: 'Window rows',
    tip: 'How many arcade_player_match_performance rows were counted. One row per match the player played for that team.',
  },
  {
    key: 'cap',
    label: 'Cap',
    tip: 'Completed team matches in the window. Usually 3, or 20 when the extended fallback is used.',
  },
  {
    key: 'this',
    label: 'This match',
    tip: 'Impact for this fixture once a performance row exists. Often empty before the game.',
  },
];

function getAuthHeader() {
  const sessionToken = sessionStorage.getItem('sessionToken');
  return sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
}

function squadPlayerLabel(player) {
  return player?.name ?? player?.player_name ?? player?.title ?? '';
}

function ImpactCells({ impact }) {
  return (
    <>
      <TableCell>{formatImpactValue(impact?.avg_impact_last_3_matches)}</TableCell>
      <TableCell>{formatImpactCount(impact?.last_3_window_entries)}</TableCell>
      <TableCell>{formatImpactCount(impact?.form_completed_matches_cap)}</TableCell>
      <TableCell>{formatImpactValue(impact?.impact_this_match)}</TableCell>
    </>
  );
}

function ImpactHeaderCells() {
  return IMPACT_COLUMNS.map((column) => (
    <TableCell key={column.key}>
      <Tooltip title={column.tip}>
        <span>{column.label}</span>
      </Tooltip>
    </TableCell>
  ));
}

function PlayerIdentity({ displayById, playerId, snapshot, squadName, showIdLine = true }) {
  const displayEntry = getDisplayEntry(displayById, playerId);
  const primary = playerPrimaryName({ snapshot, displayEntry, squadName, playerId });
  const snapShort = snapshot?.player_short_name ? String(snapshot.player_short_name).trim() : '';
  const displayShort =
    displayEntry?.short_name && displayEntry?.name && String(displayEntry.short_name).trim() !== String(displayEntry.name).trim()
      ? String(displayEntry.short_name)
      : '';
  const short = snapShort && snapShort !== primary.trim() ? snapShort : displayShort;
  const image = snapshot?.player_image_url || '';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Avatar src={image || undefined} alt="" sx={{ width: 28, height: 28, fontSize: 12 }}>
        {primary.slice(0, 1)}
      </Avatar>
      <Box>
        <Typography variant="body2" component="span">
          {primary}
        </Typography>
        {short ? (
          <Typography variant="caption" color="text.secondary" display="block">
            {short}
          </Typography>
        ) : null}
        {showIdLine ? (
          <Typography variant="caption" color="text.secondary" display="block">
            ID {playerId}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function TeamLineupSection({
  lineupMeta,
  config,
  playerDisplayById,
  nameByPlayerId,
  playingEleven,
  onPatchResponse,
  onRefresh,
  showSnackbar,
}) {
  const matchId = lineupMeta?.match_id;
  const forTeamId = config?.for_team_id;
  const oppId = opponentTeamId(lineupMeta, forTeamId);
  const userSideLabel = franchiseLabel(lineupMeta, forTeamId);
  const opponentLabel = oppId != null ? franchiseLabel(lineupMeta, oppId) : null;

  const [systemSlots, setSystemSlots] = useState(() => buildInitialSystemFive(config));
  const [pickRows, setPickRows] = useState(() => userPickEntries(config));
  const [priorities, setPriorities] = useState({});
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceSlot, setReplaceSlot] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [savingSystem, setSavingSystem] = useState(false);
  const [savingPick, setSavingPick] = useState(false);

  useEffect(() => {
    setSystemSlots(buildInitialSystemFive(config));
  }, [config]);

  useEffect(() => {
    const rows = userPickEntries(config);
    setPickRows(rows);
    const next = {};
    rows.forEach((row, index) => {
      next[row.player_id] = index + 1;
    });
    setPriorities(next);
  }, [config]);

  const selectedSystemById = useMemo(() => {
    const map = new Map();
    systemSelectedEntries(config).forEach((entry) => map.set(entry.player_id, entry));
    return map;
  }, [config]);

  const snapshotForSystemPlayer = useCallback(
    (playerId) => {
      const selected = selectedSystemById.get(Number(playerId));
      if (selected) return selected;
      return findSnapshotEntry(
        [config?.manual_system_players_snapshot, config?.auto_system_players_snapshot],
        playerId,
      );
    },
    [config, selectedSystemById],
  );

  const opponentPool = useMemo(
    () => buildOpponentPickerPool(playingEleven, oppId, config),
    [playingEleven, oppId, config],
  );

  const filteredPicker = useMemo(() => {
    const query = pickerSearch.trim().toLowerCase();
    if (!query) return opponentPool;
    return opponentPool.filter((player) => {
      const playerId = Number(player.player_id);
      const snapshot = snapshotForSystemPlayer(playerId);
      const displayEntry = getDisplayEntry(playerDisplayById, playerId);
      const haystack = [snapshot?.player_name, snapshot?.player_short_name, displayEntry?.name, displayEntry?.short_name, squadPlayerLabel(player)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query) || String(playerId).includes(query);
    });
  }, [opponentPool, pickerSearch, playerDisplayById, snapshotForSystemPlayer]);

  const manualSystemActive =
    Array.isArray(config?.manual_system_player_ids) && config.manual_system_player_ids.length === 5;
  const manualPickActive = userPickSnapshotSource(config) === 'manual';
  const systemSource = systemSnapshotSource(config);

  const effectiveFive = config?.effective_system_player_ids;
  const pendingEffective =
    !Array.isArray(effectiveFive) || effectiveFive.length === 0 || effectiveFive.every((id) => id == null);

  const openReplace = (slotIndex) => {
    setReplaceSlot(slotIndex);
    setPickerSearch('');
    setReplaceOpen(true);
  };

  const pickReplacement = (player) => {
    const playerId = Number(player.player_id);
    if (replaceSlot == null || Number.isNaN(playerId)) return;
    setSystemSlots((prev) => {
      const next = [...prev];
      next[replaceSlot] = playerId;
      return next;
    });
    setReplaceOpen(false);
    setReplaceSlot(null);
  };

  const handlePatchResult = async (res, successMessage) => {
    if (String(res.statusCode) === '10000') {
      showSnackbar(successMessage, 'success');
      onPatchResponse?.(res);
      await onRefresh();
      return;
    }
    if (String(res.statusCode) === '10003') {
      showSnackbar('Session expired. Please login again.', 'error');
      setTimeout(() => {
        window.location = '/login';
      }, 1500);
      return;
    }
    showSnackbar(res.message || 'Could not update lineup', 'error');
  };

  const saveManualSystem = async () => {
    if (!matchId || forTeamId == null) return;
    const five = systemSlots.map((id) => (id != null ? Number(id) : NaN));
    if (five.some((id) => Number.isNaN(id))) {
      showSnackbar('All five system slots must have a player selected.', 'warning');
      return;
    }
    if (new Set(five).size !== 5) {
      showSnackbar('System lineup requires five distinct player ids.', 'warning');
      return;
    }
    setSavingSystem(true);
    try {
      const res = await patchFanwarsLineup({
        match_id: Number(matchId),
        for_team_id: Number(forTeamId),
        manual_system_player_ids: five,
      });
      await handlePatchResult(res, 'System lineup saved.');
    } finally {
      setSavingSystem(false);
    }
  };

  const clearManualSystem = async () => {
    if (!matchId || forTeamId == null) return;
    setSavingSystem(true);
    try {
      const res = await patchFanwarsLineup({
        match_id: Number(matchId),
        for_team_id: Number(forTeamId),
        manual_system_player_ids: null,
      });
      await handlePatchResult(res, 'Reverted to auto system lineup.');
    } finally {
      setSavingSystem(false);
    }
  };

  const savePickOrder = async () => {
    if (!matchId || forTeamId == null) return;
    const ordered = orderedIdsByPriority(pickRows, priorities);
    if (!ordered.length) {
      showSnackbar('No players to order.', 'warning');
      return;
    }
    setSavingPick(true);
    try {
      const res = await patchFanwarsLineup({
        match_id: Number(matchId),
        for_team_id: Number(forTeamId),
        manual_user_pick_order: ordered,
      });
      await handlePatchResult(res, 'Pick list order saved.');
    } finally {
      setSavingPick(false);
    }
  };

  const clearPickOrder = async () => {
    if (!matchId || forTeamId == null) return;
    setSavingPick(true);
    try {
      const res = await patchFanwarsLineup({
        match_id: Number(matchId),
        for_team_id: Number(forTeamId),
        manual_user_pick_order: null,
      });
      await handlePatchResult(res, 'Reverted to auto pick order.');
    } finally {
      setSavingPick(false);
    }
  };

  const setPriority = (playerId, value) => {
    const nextValue = parseInt(String(value), 10);
    setPriorities((prev) => ({
      ...prev,
      [playerId]: Number.isNaN(nextValue) ? prev[playerId] ?? 1 : nextValue,
    }));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        When the user plays as <strong>{userSideLabel}</strong>
        {forTeamId != null ? <> (team {forTeamId})</> : null}
        {opponentLabel ? (
          <>
            {' '}
            · System slots use <strong>{opponentLabel}</strong>
            {oppId != null ? <> (team {oppId})</> : null}
          </>
        ) : null}
      </Typography>
      {(config.auto_computed_at || config.updated_at) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {config.auto_computed_at != null && <>Last auto refresh: {String(config.auto_computed_at)} · </>}
          {config.updated_at != null && <>Last save: {String(config.updated_at)}</>}
        </Typography>
      )}

      {pendingEffective ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          System slots are empty until the next arcade sync completes, or set a manual lineup of five valid opponent
          players.
        </Alert>
      ) : null}

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        System lineup (b1–b5)
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {manualSystemActive ? <Chip size="small" label="Manual override active" color="warning" /> : null}
        {systemSource === 'manual' ? (
          <Chip size="small" label="Scores from manual system snapshot" variant="outlined" />
        ) : null}
        {systemSource === 'auto' ? (
          <Chip size="small" label="Scores from auto system snapshot" variant="outlined" />
        ) : null}
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Slot</TableCell>
              <TableCell>Player</TableCell>
              <ImpactHeaderCells />
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[0, 1, 2, 3, 4].map((index) => {
              const playerId = systemSlots[index];
              const snapshot = playerId != null ? snapshotForSystemPlayer(playerId) : null;
              const squadName = playerId != null ? nameByPlayerId.get(Number(playerId)) : '';
              return (
                <TableRow key={index}>
                  <TableCell>b{index + 1}</TableCell>
                  <TableCell>
                    {playerId != null ? (
                      <PlayerIdentity
                        displayById={playerDisplayById}
                        playerId={playerId}
                        snapshot={snapshot}
                        squadName={squadName}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <ImpactCells impact={snapshot?.impact} />
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={() => openReplace(index)}>
                      Replace
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        <Button variant="contained" disabled={savingSystem} onClick={saveManualSystem}>
          {savingSystem ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              Saving…
            </Box>
          ) : (
            'Save manual system five'
          )}
        </Button>
        <Button variant="outlined" disabled={savingSystem || !manualSystemActive} onClick={clearManualSystem}>
          Clear system override (use auto)
        </Button>
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        User pick list priority
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {manualPickActive ? <Chip size="small" label="Manual order active" color="warning" /> : null}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {manualPickActive
          ? 'This table is the saved manual order. Players left out of that list still appear after it in the app, sorted by live impact. Include every squad player for a fixed order. Priority 1 is shown first. Ties save by player id ascending.'
          : 'Default order follows the auto impact snapshot. Priority 1 is shown first. On save, ties break by player id ascending. Include every squad player for a fixed order.'}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Player</TableCell>
              <ImpactHeaderCells />
              <TableCell width={120}>Priority</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pickRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">
                    No pick snapshot yet. Lineup data will appear after the next arcade sync for this match, or use Sync
                    from DB.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pickRows.map((row) => (
                <TableRow key={row.player_id}>
                  <TableCell>
                    <PlayerIdentity
                      displayById={playerDisplayById}
                      playerId={row.player_id}
                      snapshot={row}
                      squadName={nameByPlayerId.get(row.player_id)}
                    />
                  </TableCell>
                  <ImpactCells impact={row.impact} />
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={priorities[row.player_id] ?? ''}
                      onChange={(e) => setPriority(row.player_id, e.target.value)}
                      inputProps={{ min: 1, 'aria-label': `Priority for ${row.player_name || row.player_id}` }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" disabled={savingPick || pickRows.length === 0} onClick={savePickOrder}>
          {savingPick ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              Saving…
            </Box>
          ) : (
            'Save pick order'
          )}
        </Button>
        <Button variant="outlined" disabled={savingPick || !manualPickActive} onClick={clearPickOrder}>
          Clear pick order (use auto)
        </Button>
      </Box>

      <Dialog open={replaceOpen} onClose={() => setReplaceOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Replace slot {replaceSlot != null ? `b${replaceSlot + 1}` : ''}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            margin="normal"
            label="Search players"
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Opponent players for {opponentLabel || 'the other franchise'}: playing eleven where the team matches, plus
            anyone already stored on this system lineup. The server still checks squad, playing eleven, match
            performance, or the stored system five.
          </Typography>
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell align="right">Pick</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPicker.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography color="text.secondary" variant="body2">
                        No players match. Clear the search, run Sync from DB, or confirm this side has a playing eleven
                        or stored system ids.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPicker.map((player) => (
                    <TableRow key={player.player_id}>
                      <TableCell>
                        <PlayerIdentity
                          displayById={playerDisplayById}
                          playerId={player.player_id}
                          snapshot={snapshotForSystemPlayer(player.player_id)}
                          squadName={squadPlayerLabel(player)}
                          showIdLine={false}
                        />
                        {!player._pickerFromSquad ? (
                          <Typography variant="caption" color="warning.main" display="block">
                            Stored lineup only
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{player.player_id}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => pickReplacement(player)}>
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function FanWarsLineup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMid = searchParams.get('match_id') || '';

  const [matchIdInput, setMatchIdInput] = useState(initialMid);
  const [lineupMeta, setLineupMeta] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [playingEleven, setPlayingEleven] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const nameByPlayerId = useMemo(() => {
    const names = new Map();
    playingEleven.forEach((player) => {
      if (player?.player_id != null) names.set(Number(player.player_id), squadPlayerLabel(player));
    });
    return names;
  }, [playingEleven]);

  const loadPlayingEleven = useCallback(async (matchId) => {
    if (!matchId) {
      setPlayingEleven([]);
      return;
    }
    try {
      const res = await fetch(
        `${BASE_URL}/players/live/playing-eleven?mid=${encodeURIComponent(matchId)}&limit=500&offset=0`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
        },
      );
      const data = await res.json().catch(() => ({}));
      const list = data?.data?.playingEleven ?? data?.data?.players ?? data?.players ?? [];
      setPlayingEleven(Array.isArray(list) ? list : []);
    } catch {
      setPlayingEleven([]);
    }
  }, []);

  const fetchLineup = useCallback(
    async (midRaw, opts = {}) => {
      const { refresh = false, keepTab = false, preserveOnError = false } = opts;
      const matchId = String(midRaw || '').trim();
      if (!matchId) {
        showSnackbar('Enter a match id.', 'warning');
        return;
      }
      setLoading(true);
      try {
        const res = await getFanwarsLineup(matchId, { refresh });
        if (String(res.statusCode) === '10000') {
          const payload = normalizeLineupPayload(res);
          setLineupMeta(payload);
          const nextConfigs = sortConfigsForMatch(payload?.configs, payload);
          setConfigs(nextConfigs);
          setTab((prev) => {
            if (!keepTab) return 0;
            return prev < nextConfigs.length ? prev : 0;
          });
          setSearchParams({ match_id: matchId });
          await loadPlayingEleven(matchId);
          if (refresh && !payload?.sync_error) {
            showSnackbar('Lineup sync from DB completed. Review configs below.', 'success');
          }
          if (refresh && payload?.sync_error) {
            showSnackbar('Sync from DB returned an error. See the details above.', 'warning');
          }
        } else if (String(res.statusCode) === '10003') {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 1500);
        } else if (!preserveOnError) {
          setLineupMeta(null);
          setConfigs([]);
          showSnackbar(res.message || 'Failed to load lineup', 'error');
        } else {
          const detail = res?.message ? ` ${res.message}` : '';
          showSnackbar(`Saved, but the lineup could not be reloaded.${detail}`, 'warning');
        }
      } finally {
        setLoading(false);
      }
    },
    [loadPlayingEleven, setSearchParams, showSnackbar],
  );

  useEffect(() => {
    if (initialMid) {
      fetchLineup(initialMid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRefresh = useCallback(async () => {
    const matchId = lineupMeta?.match_id ?? matchIdInput;
    if (matchId) await fetchLineup(matchId, { keepTab: true, preserveOnError: true });
  }, [fetchLineup, lineupMeta?.match_id, matchIdInput]);

  const onPatchResponse = useCallback((res) => {
    const patchMap = res?.data?.player_display_by_id;
    if (patchMap != null && typeof patchMap === 'object' && !Array.isArray(patchMap)) {
      setLineupMeta((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          player_display_by_id: { ...(prev.player_display_by_id || {}), ...patchMap },
        };
      });
    }
    const patched = res?.data?.config;
    if (patched == null || patched.for_team_id == null) return;
    setConfigs((prev) => {
      const index = prev.findIndex((row) => Number(row.for_team_id) === Number(patched.for_team_id));
      if (index < 0) return prev;
      const next = [...prev];
      next[index] = { ...next[index], ...patched };
      return next;
    });
  }, []);

  const teamAChip = lineupMeta ? sideChipLabel(lineupMeta, 'a') : null;
  const teamBChip = lineupMeta ? sideChipLabel(lineupMeta, 'b') : null;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3>FanWars lineup (Arcade)</h3>
              </div>
            </div>
          </div>
        </div>
        <div className="container-fluid">
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Load a match by <code>matches.match_id</code>. Each side has its own system five (b1–b5) and user pick
              priority. API: <code>GET/PATCH /admin/arcade/fanwars-lineup</code>. Names come from snapshot{' '}
              <code>player_name</code> and <code>player_display_by_id</code>. Use <strong>Sync from DB</strong> (
              <code>refresh=1</code>) when a fixture has no rows yet.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Match ID"
                size="small"
                value={matchIdInput}
                onChange={(e) => setMatchIdInput(e.target.value)}
                sx={{ minWidth: 200 }}
              />
              <Button variant="contained" disabled={loading} onClick={() => fetchLineup(matchIdInput)}>
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Load'}
              </Button>
              <Button
                variant="outlined"
                disabled={loading || !String(matchIdInput || '').trim()}
                onClick={() => fetchLineup(matchIdInput, { refresh: true, keepTab: true })}
                title="GET with refresh=1 — re-runs lineup sync from DB"
              >
                Sync from DB
              </Button>
            </Box>
          </Paper>

          {lineupMeta && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Match {lineupMeta.match_id}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {lineupMeta.arcade_match_id != null && (
                  <Chip label={`Arcade match: ${lineupMeta.arcade_match_id}`} size="small" />
                )}
                <Chip
                  label={`FanWars: ${lineupMeta.fanwars_enabled ? 'enabled' : 'disabled'}`}
                  size="small"
                  color={lineupMeta.fanwars_enabled ? 'success' : 'default'}
                />
                {teamAChip ? <Chip label={teamAChip} size="small" /> : null}
                {teamBChip ? <Chip label={teamBChip} size="small" /> : null}
                {lineupMeta.on_demand_sync_attempted ? (
                  <Chip label="On-demand sync ran (this request)" size="small" color="info" variant="outlined" />
                ) : null}
              </Box>
              {lineupMeta.sync_error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Sync error: {String(lineupMeta.sync_error)}
                </Alert>
              ) : null}
              {lineupMeta.sync_skipped != null && lineupMeta.sync_skipped !== '' ? (
                <Alert severity="warning" sx={{ mt: lineupMeta.sync_error ? 1 : 2 }}>
                  Sync skipped: {String(lineupMeta.sync_skipped)}
                </Alert>
              ) : null}
            </Paper>
          )}

          {lineupMeta && configs.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Lineup data will appear after the next arcade sync. If FanWars is enabled, this request may already have
              tried an on-demand sync. Use <strong>Sync from DB</strong> for older fixtures outside the live cron
              window.
            </Alert>
          )}

          {configs.length > 0 && (
            <>
              <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {configs.map((row, index) => (
                  <Tab
                    key={row.for_team_id ?? index}
                    label={`Play as ${franchiseLabel(lineupMeta, row.for_team_id)}`}
                  />
                ))}
              </Tabs>
              {configs[tab] && (
                <TeamLineupSection
                  lineupMeta={lineupMeta}
                  config={configs[tab]}
                  playerDisplayById={lineupMeta.player_display_by_id || {}}
                  nameByPlayerId={nameByPlayerId}
                  playingEleven={playingEleven}
                  onPatchResponse={onPatchResponse}
                  onRefresh={onRefresh}
                  showSnackbar={showSnackbar}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((current) => ({ ...current, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default FanWarsLineup;
